import React, { useState, useEffect, useRef } from "react";
import i18n from "i18next";
import { LuFileText, LuLoaderCircle, LuCircleX, LuCopy, LuCheck, LuMic } from "react-icons/lu";
import AppModal from "../common/modal/AppModal";
// Styled like the normal AI voice sound modal (AddSoundModal / AIVoiceTab): the
// same modal shell (`addSoundModal`) and the same text card/textarea classes.
import "../add_sound_modal/_addSoundModal.scss";

// Transcribe popup. Opening it does NOT auto-start a job:
//   - existing transcript  → EDITABLE view: the text can be corrected in place
//                            (Save → onSave), copied to the clipboard, or taken
//                            straight to the "Anlatım Sesi" modal (onOpenNarration
//                            receives whatever is in the textarea right now, so
//                            edits carry over even when they were not saved).
//   - none yet             → "Start transcription" (hands the job to the
//                            background job tray/widget; result appears here)
//   - started, no result   → "started" note (progress lives in the widget)
//   - error                → error + Retry
export default function TranscriptModal({
  visible,
  transcript,
  started,
  error,
  onStart,
  onClose,
  onRetry,
  onSave,
  onOpenNarration,
}) {
  const [text, setText] = useState(transcript || "");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(null);
  const textareaRef = useRef(null);

  // Reseed the editor whenever the popup is (re)opened or a new transcript
  // lands — otherwise a stale edit would leak into the next box's transcript.
  useEffect(() => {
    if (visible) {
      setText(transcript || "");
      setCopied(false);
    }
  }, [visible, transcript]);

  useEffect(() => () => clearTimeout(copyTimer.current), []);

  if (!visible) return null;

  const hasTranscript = transcript != null && transcript !== undefined;

  const ghostBtn = (label, onClick, disabled) => (
    <button
      type="button"
      className="app-modal__btn app-modal__btn--ghost"
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );

  // Clipboard API needs a secure context; fall back to the textarea + execCommand
  // path so copying still works over plain http (local/dev previews).
  const copyText = async () => {
    const value = text || "";
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else if (textareaRef.current) {
        textareaRef.current.select();
        document.execCommand("copy");
        textareaRef.current.setSelectionRange(value.length, value.length);
      }
      setCopied(true);
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 1800);
    } catch {
      /* copying is best-effort — never break the modal over it */
    }
  };

  const save = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave(text);
    } finally {
      setSaving(false);
    }
  };

  let body;
  let footer;

  if (error) {
    body = (
      <div style={centerBox}>
        <LuCircleX size={28} color="#ef4444" />
        <span style={{ fontSize: 13, color: "#ef4444", fontWeight: 600 }}>
          {i18n.t("MerlinVideoTranscribeFailed")}
        </span>
      </div>
    );
    footer = (
      <div className="app-modal__footer">
        {onRetry && ghostBtn(i18n.t("TranscribeRetry"), onRetry)}
        {ghostBtn(i18n.t("TranscriptModal_Close"), onClose)}
      </div>
    );
  } else if (hasTranscript) {
    // Editable transcript, mirroring AIVoiceTab's text card.
    body = (
      <div className="add-sound-modal__ai-voice-card">
        <div className="add-sound-modal__ai-voice-field">
          <div className="add-sound-modal__ai-voice-label-row">
            <button type="button" style={copyBtn} onClick={copyText}>
              {copied ? <LuCheck size={14} color="#16a34a" /> : <LuCopy size={14} />}
              <span style={{ color: copied ? "#16a34a" : "#475569" }}>
                {copied ? i18n.t("TranscriptModal_Copied") : i18n.t("TranscriptModal_Copy")}
              </span>
            </button>
            <span className="add-sound-modal__ai-voice-count">
              {text.length} {i18n.t("chars") || "karakter"}
            </span>
          </div>
          <textarea
            ref={textareaRef}
            className="add-sound-modal__ai-voice-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            // Fixed height (normal popup size) → text scrolls inside when long.
            style={{ height: 300, minHeight: 0, lineHeight: 1.6, resize: "none" }}
          />
        </div>
      </div>
    );
    footer = (
      <div className="app-modal__footer">
        {ghostBtn(i18n.t("TranscriptModal_Close"), onClose, saving)}
        {onOpenNarration && (
          <button
            type="button"
            className="app-modal__btn app-modal__btn--ghost"
            onClick={() => onOpenNarration(text)}
            disabled={saving || !text.trim()}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <LuMic size={15} />
            {i18n.t("NarrationSound")}
          </button>
        )}
        <button
          type="button"
          className="app-modal__btn app-modal__btn--primary"
          onClick={save}
          disabled={saving}
        >
          {saving ? i18n.t("TranscriptModal_Saving") : i18n.t("TranscriptModal_Save")}
        </button>
      </div>
    );
  } else if (started) {
    body = (
      <div style={centerBox}>
        <LuLoaderCircle
          size={26}
          color="#3b82f6"
          style={{ animation: "spin 1s linear infinite" }}
        />
        <span style={{ fontSize: 13, color: "#64748b", fontWeight: 600, textAlign: "center" }}>
          {i18n.t("TranscribeStartedHint")}
        </span>
      </div>
    );
    footer = (
      <div className="app-modal__footer">{ghostBtn(i18n.t("TranscriptModal_Close"), onClose)}</div>
    );
  } else {
    body = (
      <div style={centerBox}>
        <LuFileText size={26} color="#94a3b8" />
        <span style={{ fontSize: 13, color: "#64748b", textAlign: "center", maxWidth: 380 }}>
          {i18n.t("TranscribeEmptyHint")}
        </span>
      </div>
    );
    footer = (
      <div className="app-modal__footer">
        {ghostBtn(i18n.t("TranscriptModal_Close"), onClose)}
        <button type="button" className="app-modal__btn app-modal__btn--primary" onClick={onStart}>
          {i18n.t("TranscribeStart")}
        </button>
      </div>
    );
  }

  return (
    <AppModal
      show={visible}
      onClose={onClose}
      size="lg"
      className="addSoundModal"
      bodyProps={{ style: { padding: "18px 22px" } }}
      showSceneToggle={false}
      title={i18n.t("TranscriptModal_Title")}
      footer={footer}
    >
      {body}
    </AppModal>
  );
}

const centerBox = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  padding: "30px 0",
};

const copyBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  border: "1px solid #e2e8f0",
  background: "#ffffff",
  borderRadius: 8,
  padding: "4px 10px",
  fontSize: 12,
  fontWeight: 600,
  color: "#475569",
  cursor: "pointer",
};
